// Sprint 4A — CFPB Escalation panel inside Admin Dispute Queue area.
// Admin opens an analyzed credit profile, selects items, triggers CFPB draft.
// This page lives at /dashboard/admin/cfpb and lists round-3 cfpb-recipient
// letters with the structured complaint package downloadable per row.
import { useCallback, useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { apiCall } from "../auth/AuthContext";
import { Loader2, Shield, Download, ExternalLink, CheckSquare, Square, Sparkles } from "lucide-react";

export default function AdminCfpbQueue() {
  const [profiles, setProfiles] = useState([]);
  const [cfpbLetters, setCfpbLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProfile, setActiveProfile] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [msg, setMsg] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [activePackage, setActivePackage] = useState(null);

  const reload = useCallback(async () => {
    const [q, lq] = await Promise.all([
      apiCall("/api/admin/credit/queue").catch(() => ({ records: [] })),
      apiCall("/api/admin/disputes/queue?status=draft").catch(() => ({ records: [] })),
    ]);
    const lq2 = await apiCall("/api/admin/disputes/queue?status=sent").catch(() => ({ records: [] }));
    const all = [...(lq.records || []), ...(lq2.records || [])];
    return {
      profiles: q.records || [],
      cfpb: all.filter((l) => l.recipient_type === "cfpb"),
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const d = await reload();
      if (cancelled) return;
      setProfiles(d.profiles);
      setCfpbLetters(d.cfpb);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reload]);

  const toggle = (i) => {
    const next = new Set(selected);
    next.has(i) ? next.delete(i) : next.add(i);
    setSelected(next);
  };

  const draft = async () => {
    if (!activeProfile || selected.size === 0) return;
    setDrafting(true); setMsg("");
    try {
      const d = await apiCall(`/api/admin/cfpb/draft/${activeProfile.id}`, {
        method: "POST", body: JSON.stringify({ item_indices: [...selected] }),
      });
      setMsg(`✔ CFPB draft created. Letter ID ${d.letter_id}. Review in Dispute Letters.`);
      const fresh = await reload();
      setCfpbLetters(fresh.cfpb);
      setSelected(new Set());
    } catch (e) {
      setMsg(`× ${e.message || "Draft failed"}`);
    } finally { setDrafting(false); }
  };

  const candidates = activeProfile?.profile?.dispute_candidates || [];

  return (
    <AdminLayout title="CFPB ESCALATION" kicker="Sprint 4A · 7D · Round 3 federal complaint">
      {loading ? <div className="py-20 flex items-center justify-center text-jc-muted gap-3"><Loader2 className="animate-spin" size={18}/> Loading…</div> :
       (
        <div className="grid lg:grid-cols-12 gap-6">
          {/* LEFT — pick a profile */}
          <div className="lg:col-span-4 space-y-2" data-testid="cfpb-profile-list">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-jc-gold mb-2">◆ Eligible profiles</p>
            {profiles.length === 0 && <p className="text-sm text-jc-muted">No analyzed credit profiles yet.</p>}
            {profiles.map((p) => (
              <button key={p.id} onClick={() => { setActiveProfile(p); setSelected(new Set()); setMsg(""); }}
                data-testid={`cfpb-profile-${p.id}`}
                className={`w-full text-left border ${activeProfile?.id === p.id ? "border-jc-gold bg-jc-gold/5" : "border-jc-gold/15 hover:border-jc-gold/40"} px-4 py-3 transition-colors`}>
                <p className="font-display text-jc-white">{p.client_first_name || p.client_email}</p>
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-jc-muted mt-0.5">{(p.profile?.dispute_candidates || []).length} candidates</p>
              </button>
            ))}
          </div>

          {/* MIDDLE — pick items + draft */}
          <div className="lg:col-span-4">
            {!activeProfile ? (
              <div className="py-12 text-center border border-dashed border-jc-gold/15 rounded-xl text-jc-muted">Select a profile to begin.</div>
            ) : (
              <div className="border border-jc-gold/20 bg-jc-navy/40 p-5 rounded-xl">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-jc-gold mb-2">◆ Select unresolved items</p>
                {candidates.length === 0 ? <p className="text-sm text-jc-muted">No candidates on this profile.</p> :
                  <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                    {candidates.map((c, i) => (
                      <label key={i} onClick={() => toggle(i)} className="flex items-start gap-2 border border-jc-gold/15 px-3 py-2 cursor-pointer hover:border-jc-gold/40" data-testid={`cfpb-candidate-${i}`}>
                        <span className="text-jc-gold mt-0.5">{selected.has(i) ? <CheckSquare size={14}/> : <Square size={14}/>}</span>
                        <div className="flex-1 text-xs">
                          <p className="text-jc-white">{c.creditor_or_furnisher}</p>
                          <p className="text-jc-muted">{(c.fcra_violation || "").slice(0, 120)}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                }
                <button data-testid="cfpb-draft-btn" disabled={drafting || selected.size === 0} onClick={draft}
                  className="mt-4 jc-btn jc-btn-primary !text-xs disabled:opacity-50">
                  {drafting ? <><Loader2 size={12} className="animate-spin"/> Drafting…</> : <><Sparkles size={12}/> Draft CFPB Package ({selected.size})</>}
                </button>
                {msg && <p className={`mt-2 text-xs ${msg.startsWith("✔") ? "text-emerald-300" : "text-red-300"}`} data-testid="cfpb-msg">{msg}</p>}
              </div>
            )}
          </div>

          {/* RIGHT — existing CFPB letters */}
          <div className="lg:col-span-4 space-y-2" data-testid="cfpb-letter-list">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-jc-gold mb-2">◆ CFPB letters drafted</p>
            {cfpbLetters.length === 0 && <p className="text-sm text-jc-muted">No CFPB letters drafted yet.</p>}
            {cfpbLetters.map((l) => (
              <div key={l.id} className="border border-jc-gold/15 px-4 py-3" data-testid={`cfpb-letter-${l.id}`}>
                <p className="font-display text-sm text-jc-white">{l.client_email}</p>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-jc-muted">Status: {l.status} · Round {l.round_number}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={async () => {
                    const d = await apiCall(`/api/admin/cfpb/package/${l.id}`).catch((e) => { setActivePackage({ error: e.message }); return null; });
                    if (d) setActivePackage(d);
                  }} className="jc-btn jc-btn-outline !text-[10px] !py-1.5 !px-2.5" data-testid={`cfpb-view-${l.id}`}>
                    <Shield size={10}/> View package
                  </button>
                  <a href="https://www.consumerfinance.gov/complaint/" target="_blank" rel="noreferrer" className="jc-btn jc-btn-outline !text-[10px] !py-1.5 !px-2.5">
                    <ExternalLink size={10}/> CFPB form
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Package preview modal */}
      {activePackage && (
        <div className="fixed inset-0 bg-jc-black/80 backdrop-blur z-50 flex items-center justify-center p-6" onClick={() => setActivePackage(null)}>
          <div className="bg-jc-navy border border-jc-gold/40 max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 rounded-xl" onClick={(e) => e.stopPropagation()} data-testid="cfpb-package-modal">
            <h3 className="font-display text-xl text-jc-white">CFPB Complaint Package</h3>
            {activePackage.error ? <p className="text-red-300 mt-3">{activePackage.error}</p> :
              <pre className="mt-4 text-xs text-jc-white whitespace-pre-wrap font-mono bg-jc-black/60 p-4 rounded">{JSON.stringify(activePackage.package || activePackage, null, 2)}</pre>}
            <div className="mt-4 flex gap-2">
              <button onClick={() => { navigator.clipboard?.writeText(JSON.stringify(activePackage.package || activePackage, null, 2)); }} className="jc-btn jc-btn-primary !text-xs"><Download size={12}/> Copy JSON</button>
              <button onClick={() => setActivePackage(null)} className="jc-btn jc-btn-outline !text-xs">Close</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
